import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CenterModals from "../centerModals";
import CenterBreadcrumb from "./centerBreadcrumb";
import { useCenter } from "../hooks/useCenter";
import { useFeeConfig } from "../../../feeconfig/courseFeeConfig/hooks/useFeeConfig";
import { useRegistrationFeeConfig } from "../../../feeconfig/registrationFeeConfig/hooks/useRegistrationFeeConfig";
import { formatDateToYMD } from "../../../../core/utils/dateFormatter";

interface AttachedCenter {
  id: number;
  // add other properties if needed
}

const CenterDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { useGetCenterById } = useCenter({ skip: true });
  const { data: centerData, isLoading, error } = useGetCenterById(id || "");
  const { feeConfigs } = useFeeConfig();
  const { feeConfigs: registrationFeeConfigs } = useRegistrationFeeConfig();
  const [expandedFee, setExpandedFee] = useState<number | null>(null);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error loading center details</div>;
  }

  const center = centerData?.data.center;

  // Get fee configs attached to this center
  const centerFeeConfigs = feeConfigs.filter(
    (config) =>
      Array.isArray(config.atachedToCenters) &&
      config.atachedToCenters.some(
        (attachedCenter: AttachedCenter | null) =>
          attachedCenter && attachedCenter.id === Number(id)
      )
  );

  // Get registration fee configs attached to this center
  const centerRegistrationFeeConfigs = registrationFeeConfigs.filter(
    (config) =>
      Array.isArray(config.atachedToCenters) &&
      config.atachedToCenters.some(
        (attachedCenter: AttachedCenter | null) =>
          attachedCenter && attachedCenter.id === Number(id)
      )
  );

  // Group registration fees by course ID
  const groupedRegistrationFees = centerRegistrationFeeConfigs.reduce(
    (acc: any, fee) => {
      const courseId = fee.course?.id || "no-course";
      if (!acc[courseId]) {
        acc[courseId] = [];
      }
      acc[courseId].push(fee);
      return acc;
    },
    {}
  );

  const toggleAccordion = (feeId: number) => {
    setExpandedFee(expandedFee === feeId ? null : feeId);
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            {/* Page Header */}
            <CenterBreadcrumb centerId={id} />
            {/* /Page Header */}
          </div>
          <div className="row">
            {/* Center Core Information */}
            <div className="col-xxl-12">
              <div className="card">
                <div className="card-header bg-light">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-building-community fs-16" />
                      </span>
                      <h4 className="text-dark mb-0">Center Information</h4>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <Link
                        className="btn btn-primary btn-sm"
                        to={all_routes.centerUsersId.replace(":id", id || "")}
                      >
                        <i className="ti ti-eye me-1"></i>
                        View Users
                      </Link>
                      <Link
                        to={all_routes.addUser.replace(":id", id || "")}
                        className="btn btn-primary btn-sm"
                      >
                        <i className="ti ti-user-plus me-1"></i>
                        Add User
                      </Link>
                      <Link
                        to={all_routes.centerAnalytics.replace(":id", id || "")}
                        className="btn btn-info btn-sm"
                      >
                        <i className="ti ti-chart-bar me-1"></i>
                        View Analytics
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover">
                      <tbody>
                        <tr>
                          <th style={{ width: "200px" }} className="bg-light">
                            Center Name
                          </th>
                          <td>{center?.name}</td>
                          <th style={{ width: "200px" }} className="bg-light">
                            Center ID
                          </th>
                          <td>{center?.code}</td>
                        </tr>
                        <tr>
                          <th className="bg-light">Zone</th>
                          <td>{center?.zone}</td>
                          <th className="bg-light">District</th>
                          <td>{center?.district}</td>
                        </tr>
                        <tr>
                          <th className="bg-light">Type</th>
                          <td>{center?.type}</td>
                          <th className="bg-light">Address</th>
                          <td colSpan={3}>{center?.address}</td>
                        </tr>
                        <tr>
                          <th className="bg-light">Email</th>
                          <td>{center?.email}</td>
                          <th className="bg-light">Phone</th>
                          <td>{center?.phone}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            {/* /Center Core Information */}

            {/* Fee Structure Accordion */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Fee Structure</h5>
                </div>
                <div className="card-body">
                  <div className="accordion" id="feeAccordion">
                    {/* Registration Fees Section - Separated by Course */}
                    {Object.keys(groupedRegistrationFees).length > 0
                      ? Object.entries(groupedRegistrationFees).map(
                        ([courseId, fees]: [string, any], index) => {
                          const firstFee = fees[0];
                          return (
                            <div
                              className="accordion-item"
                              key={`reg-${courseId}`}
                            >
                              <h2 className="accordion-header">
                                <button
                                  className={`accordion-button ${expandedFee === -parseInt(courseId)
                                    ? ""
                                    : "collapsed"
                                    }`}
                                  type="button"
                                  onClick={() =>
                                    toggleAccordion(-parseInt(courseId))
                                  }
                                >
                                  <div className="d-flex flex-wrap justify-content-between w-100 gap-2">
                                    <div className="d-flex flex-wrap gap-2">
                                      <span className="fw-medium">
                                        Registration Fee
                                      </span>
                                      <span className="text-muted">
                                        {"-"}{" "}
                                        {firstFee.course?.name || "No Course"}
                                      </span>
                                    </div>
                                  </div>
                                </button>
                              </h2>
                              <div
                                className={`accordion-collapse collapse ${expandedFee === -parseInt(courseId)
                                  ? "show"
                                  : ""
                                  }`}
                              >
                                <div className="accordion-body">
                                  <div className="table-responsive">
                                    <table className="table table-bordered mb-0">
                                      <thead>
                                        <tr>
                                          <th>Course</th>
                                          <th>Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {fees.map((regFee: any) => (
                                          <tr key={regFee.id}>
                                            <td>{regFee.course.name}</td>
                                            <td>
                                              ₹
                                              {parseFloat(regFee.fee).toFixed(
                                                2
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      )
                      : null}

                    {/* Course Fees Section */}
                    {centerFeeConfigs.length > 0
                      ? centerFeeConfigs.map((config) => (
                        <div className="accordion-item" key={config.id}>
                          <h2 className="accordion-header">
                            <button
                              className={`accordion-button ${expandedFee === config.id ? "" : "collapsed"
                                }`}
                              type="button"
                              onClick={() => toggleAccordion(config.id)}
                            >
                              <div className="d-flex flex-wrap justify-content-between w-100 gap-2">
                                <div className="d-flex flex-wrap gap-2">
                                  <span className="fw-medium">
                                    {config.feeType.type === "coursefee"
                                      ? "Course Fee"
                                      : "Other Fee"}
                                  </span>
                                  <span className="text-muted">
                                    {"-"} {config.course?.name || "No Course"}
                                  </span>
                                </div>
                              </div>
                            </button>
                          </h2>
                          <div
                            className={`accordion-collapse collapse ${expandedFee === config.id ? "show" : ""
                              }`}
                          >
                            <div className="accordion-body">
                              <div className="table-responsive">
                                <table className="table table-bordered mb-0">
                                  <thead>
                                    <tr>
                                      <th>Levels</th>
                                      <th>Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {config.stageFees?.map(
                                      (stageFee, index) => (
                                        <tr key={index}>
                                          <td>
                                            {stageFee.stage ||
                                              `Level ${index + 1}`}
                                          </td>
                                          <td>₹{stageFee.amount}</td>
                                        </tr>
                                      )
                                    ) || (
                                        <tr>
                                          <td
                                            colSpan={2}
                                            className="text-center"
                                          >
                                            No level fees configured
                                          </td>
                                        </tr>
                                      )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                      : null}

                    {/* Fallback if no fee configs exist */}
                    {centerFeeConfigs.length === 0 &&
                      Object.keys(groupedRegistrationFees).length === 0 && (
                        <div className="text-center d-flex flex-column gap-3 align-items-center p-3">
                          <div className="d-flex align-items-center gap-2 text-muted">
                            <i className="ti ti-alert-circle fs-4"></i>
                            No fee configurations found!
                          </div>
                          <div className="d-flex flex-column flex-sm-row gap-3">
                            <Link
                              to={all_routes.feesConfiguration}
                              className="btn btn-primary"
                            >
                              <i className="ti ti-settings me-1"></i>
                              Configure Course Fee
                            </Link>
                            <Link
                              to={all_routes.courseRegistrationFee}
                              className="btn btn-primary"
                            >
                              <i className="ti ti-settings me-1"></i>
                              Configure Registration Fee
                            </Link>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
            {/* /Fee Structure Accordion */}
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      <CenterModals centerId={id} />
    </>
  );
};

export default CenterDetails;
